import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkSessionExpiration, resetInactivityTimer } from './sessionManager.js';
import PatientInfoService from '../patientInfo/patientInfoService.js';
import '../loginServices/loginStyle.css';

const patientService = new PatientInfoService();

const LoginPatient = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [message, setMessage] = useState('');
  const [messageStyle, setMessageStyle] = useState('');
  const [spinnerVisible, setSpinnerVisible] = useState(false);

  const [isResetMode, setIsResetMode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    checkSessionExpiration();
  }, []);

  const getFriendlyErrorMessage = (error) => {
    const rawMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.response?.data ||
      error?.message ||
      '';

    if (typeof rawMessage === 'string') {
      if (
        rawMessage.toLowerCase().includes('patient not found') ||
        rawMessage.toLowerCase().includes('account') ||
        rawMessage.toLowerCase().includes('username') ||
        rawMessage.toLowerCase().includes('email')
      ) {
        return rawMessage;
      }

      return rawMessage || 'Something went wrong. Please try again.';
    }

    return 'Something went wrong. Please try again.';
  };

  const storePatientLogin = (loginResponse, fallbackUsernameOrEmail = '') => {
    if (!loginResponse?.token) {
      throw new Error('Patient login did not return a token.');
    }

    const realUsername = loginResponse.username || fallbackUsernameOrEmail;
    const patientId = loginResponse.patientId;
    const role = loginResponse.role || 'ROLE_PATIENT';

    if (!patientId) {
      throw new Error('Patient login did not return a patient ID.');
    }

    localStorage.setItem('jwt', loginResponse.token);
    localStorage.setItem('role', role);
    localStorage.setItem('username', realUsername);
    localStorage.setItem('patientId', patientId);

    sessionStorage.setItem('sessionExpires', Date.now() + 30 * 60 * 1000);

    window.dispatchEvent(new Event('userUpdated'));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    const cleanedInput = usernameOrEmail.trim();

    if (!cleanedInput || !password) {
      setMessage('Please enter your username or email and password.');
      setMessageStyle('red');
      return;
    }

    setSpinnerVisible(true);
    setIsLoggingIn(true);
    setMessage('');

    const loginRequest = {
      username: cleanedInput,
      password,
    };

    try {
      const loginResponse = await patientService.login(loginRequest);

      storePatientLogin(loginResponse, cleanedInput);

      setMessage('Login successful');
      setMessageStyle('green');
      resetInactivityTimer();

      setTimeout(() => navigate('/dashboard/patient'), 1500);
    } catch (error) {
      console.error('Login failed:', error);
      setMessage('Login failed. Please check your username/email and password.');
      setMessageStyle('red');
    } finally {
      setSpinnerVisible(false);
      setIsLoggingIn(false);
    }
  };

  const handleRequestReset = async () => {
    const cleanedInput = usernameOrEmail.trim();

    if (!cleanedInput) {
      setMessage('Please enter your username or email address first.');
      setMessageStyle('red');
      return;
    }

    setMessage('Requesting reset code...');
    setMessageStyle('black');

    try {
      await patientService.requestResetCode(cleanedInput);

      setMessage(
        'If an account exists with that username or email address, a verification code has been sent to the email address on file.'
      );
      setMessageStyle('green');
      setIsResetMode(true);
    } catch (err) {
      console.error(err);

      setMessage(
        getFriendlyErrorMessage(err) ||
          'We could not find an account with that username or email address. If you do not remember the email address on file, please contact the office for assistance.'
      );
      setMessageStyle('red');
    }
  };

  const handleResetPassword = async () => {
    const cleanedInput = usernameOrEmail.trim();

    if (!cleanedInput || !resetCode || !newPassword) {
      setMessage('Please enter your username/email, verification code, and new password.');
      setMessageStyle('red');
      return;
    }

    setMessage('Resetting password...');
    setMessageStyle('black');

    try {
      await patientService.verifyResetCodeAndChangePassword({
        usernameOrEmail: cleanedInput,
        code: resetCode,
        newPassword,
      });

      const loginResponse = await patientService.login({
        username: cleanedInput,
        password: newPassword,
      });

      storePatientLogin(loginResponse, cleanedInput);

      setMessage('Password reset successful. Logging in...');
      setMessageStyle('green');
      resetInactivityTimer();

      setTimeout(() => navigate('/dashboard/patient'), 1500);
    } catch (err) {
      console.error(err);
      setMessage(getFriendlyErrorMessage(err) || 'Failed to reset password.');
      setMessageStyle('red');
    }
  };

  const handleBackToLogin = () => {
    setIsResetMode(false);
    setResetCode('');
    setNewPassword('');
    setMessage('');
    setMessageStyle('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-title-block patient-login-title">
          <h1>{isResetMode ? 'Patient Password Reset' : 'Patient Login'}</h1>
          <p>
            {isResetMode
              ? 'Enter your verification code and new password.'
              : 'Patients should sign in here to view appointments, records, and account details.'}
          </p>
        </div>

        <form className="login-form" onSubmit={handleLoginSubmit}>
          <input
            type="text"
            placeholder="Username or Email"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            className="input-field"
            autoComplete="username"
          />

          {!isResetMode ? (
            <>
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Patient Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  autoComplete="current-password"
                />

                <label className="show-password">
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={() => setShowPassword(!showPassword)}
                    className="checkbox"
                  />
                  Show Password
                </label>
              </div>

              <button type="submit" className="login-button" disabled={isLoggingIn}>
                {isLoggingIn ? 'Logging in...' : 'Patient Login'}
              </button>

              <button
                type="button"
                className="link-button forgot-password-button"
                onClick={handleRequestReset}
                disabled={!usernameOrEmail.trim()}
              >
                Forgot Password?
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                placeholder="Verification Code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                className="input-field"
                autoComplete="one-time-code"
              />

              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-field"
                autoComplete="new-password"
              />

              <button
                type="button"
                className="login-button"
                onClick={handleResetPassword}
                disabled={!resetCode || !newPassword}
              >
                Reset Password & Login
              </button>

              <button
                type="button"
                className="link-button"
                onClick={handleBackToLogin}
              >
                Back to Login
              </button>
            </>
          )}

          {spinnerVisible && <div className="spinner"></div>}

          <div className="register-link">
            <span>Don't have an account? </span>
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/register/patient')}
            >
              Register
            </button>
          </div>
        </form>

        <div className="login-switch-note">
          <span>Are you a doctor or staff member?</span>
          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/login/doctor')}
          >
            Go to Doctor Login
          </button>
        </div>

        {message && (
          <div className="message" style={{ color: messageStyle }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPatient;